import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import FormatListBulletedOutlinedIcon from '@mui/icons-material/FormatListBulletedOutlined';
import GppBadOutlinedIcon from '@mui/icons-material/GppBadOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined';
import { showInfoToast } from 'lib/ToastHelper';
import React, { useEffect, useMemo, useState } from 'react';
import Select, { StylesConfig } from 'react-select';

import './BPAFindingPermissions.css';

type PermissionCategoryKey =
  'usedPermissions' | 'deniedPermissions' | 'unusedPermissions';

type PermissionLists = Record<PermissionCategoryKey, string[]>;

interface BPAFindingIAMPermissionsProps {
  permissions: Record<string, unknown>;
}

interface ServiceFilterOption {
  label: string;
  value: string;
}

const SERVICE_FILTER_STYLES: StylesConfig<ServiceFilterOption, false> = {
  control: (base, state) => ({
    ...base,
    backgroundColor: '#ffffff',
    borderColor: state.isFocused ? '#0878de' : '#cfd7e4',
    boxShadow: 'none',
    cursor: 'pointer',
    fontSize: '13px',
    height: '38px',
    minHeight: '38px',
    '&:hover': {
      borderColor: state.isFocused ? '#0878de' : '#aeb8c8',
    },
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: '7px',
  }),
  clearIndicator: (base) => ({
    ...base,
    padding: '7px 3px',
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  input: (base) => ({
    ...base,
    color: '#17213a',
    margin: 0,
  }),
  menu: (base) => ({
    ...base,
    fontSize: '13px',
    zIndex: 10000,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 10000,
  }),
  option: (base) => ({
    ...base,
    cursor: 'pointer',
  }),
  placeholder: (base) => ({
    ...base,
    color: '#7e88a0',
  }),
  singleValue: (base) => ({
    ...base,
    color: '#4a5670',
  }),
  valueContainer: (base) => ({
    ...base,
    height: '36px',
    padding: '0 9px',
  }),
};

const PERMISSION_CATEGORIES: Array<{
  key: PermissionCategoryKey;
  label: string;
  tone: 'used' | 'denied' | 'unused';
  emptyTitle: string;
  emptyDescription: string;
}> = [
  {
    key: 'usedPermissions',
    label: 'Used',
    tone: 'used',
    emptyTitle: 'No used permissions found',
    emptyDescription: 'There are no permissions recorded as used.',
  },
  {
    key: 'deniedPermissions',
    label: 'Denied',
    tone: 'denied',
    emptyTitle: 'No denied permissions found',
    emptyDescription: 'There are no permissions recorded as denied.',
  },
  {
    key: 'unusedPermissions',
    label: 'Unused',
    tone: 'unused',
    emptyTitle: 'No unused permissions found',
    emptyDescription: 'There are no permissions recorded as unused.',
  },
];

const normalizePermissionList = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter(
        (permission): permission is string =>
          typeof permission === 'string' && permission.trim().length > 0,
      )
    : [];

const getPermissionService = (permission: string): string => {
  const separatorIndex = permission.indexOf(':');
  return separatorIndex > 0 ? permission.slice(0, separatorIndex) : 'other';
};

const groupPermissions = (permissions: string[]): Array<[string, string[]]> => {
  const groups = new Map<string, string[]>();

  permissions.forEach((permission) => {
    const service = getPermissionService(permission);
    const servicePermissions = groups.get(service) || [];
    servicePermissions.push(permission);
    groups.set(service, servicePermissions);
  });

  return Array.from(groups.entries()).sort(([first], [second]) =>
    first.localeCompare(second),
  );
};

const getExpandedGroupDefaults = (permissionLists: PermissionLists) => {
  const defaults = new Set<string>();

  PERMISSION_CATEGORIES.forEach(({ key }) => {
    const firstGroup = groupPermissions(permissionLists[key])[0];
    if (firstGroup) defaults.add(`${key}:${firstGroup[0]}`);
  });

  return defaults;
};

const EmptyStateIcon: React.FC<{
  tone: 'used' | 'denied' | 'unused';
}> = ({ tone }) => {
  if (tone === 'used') {
    return <VerifiedUserOutlinedIcon aria-hidden="true" />;
  }
  if (tone === 'denied') {
    return <GppBadOutlinedIcon aria-hidden="true" />;
  }
  return <RemoveCircleOutlineIcon aria-hidden="true" />;
};

const BPAFindingIAMPermissions: React.FC<BPAFindingIAMPermissionsProps> = ({
  permissions,
}) => {
  const permissionLists = useMemo<PermissionLists>(
    () => ({
      usedPermissions: normalizePermissionList(permissions.usedPermissions),
      deniedPermissions: normalizePermissionList(permissions.deniedPermissions),
      unusedPermissions: normalizePermissionList(permissions.unusedPermissions),
    }),
    [permissions],
  );
  const [viewMode, setViewMode] = useState<'grouped' | 'flat'>('grouped');
  const [searchTerm, setSearchTerm] = useState('');
  const [serviceFilter, setServiceFilter] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() =>
    getExpandedGroupDefaults(permissionLists),
  );

  useEffect(() => {
    setViewMode('grouped');
    setSearchTerm('');
    setServiceFilter('');
    setExpandedGroups(getExpandedGroupDefaults(permissionLists));
  }, [permissionLists]);

  const services = useMemo(() => {
    const serviceNames = new Set<string>();
    Object.values(permissionLists).forEach((permissionList) => {
      permissionList.forEach((permission) => {
        serviceNames.add(getPermissionService(permission));
      });
    });
    return Array.from(serviceNames).sort((first, second) =>
      first.localeCompare(second),
    );
  }, [permissionLists]);
  const serviceOptions = useMemo(
    () =>
      services.map((service) => ({
        label: service,
        value: service,
      })),
    [services],
  );
  const selectedService =
    serviceOptions.find((option) => option.value === serviceFilter) || null;

  const filteredPermissionLists = useMemo<PermissionLists>(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const filterPermissions = (permissionList: string[]) =>
      permissionList.filter((permission) => {
        const matchesService =
          !serviceFilter || getPermissionService(permission) === serviceFilter;
        const matchesSearch =
          !normalizedSearch ||
          permission.toLowerCase().includes(normalizedSearch);
        return matchesService && matchesSearch;
      });

    return {
      usedPermissions: filterPermissions(permissionLists.usedPermissions),
      deniedPermissions: filterPermissions(permissionLists.deniedPermissions),
      unusedPermissions: filterPermissions(permissionLists.unusedPermissions),
    };
  }, [permissionLists, searchTerm, serviceFilter]);

  const hasActiveFilters = Boolean(searchTerm.trim() || serviceFilter);

  const clearFilters = () => {
    setSearchTerm('');
    setServiceFilter('');
  };

  const copyPermission = (permission: string) => {
    void navigator.clipboard.writeText(permission);
    showInfoToast('Permission copied to clipboard');
  };

  const toggleGroup = (groupKey: string) => {
    setExpandedGroups((currentGroups) => {
      const nextGroups = new Set(currentGroups);
      if (nextGroups.has(groupKey)) {
        nextGroups.delete(groupKey);
      } else {
        nextGroups.add(groupKey);
      }
      return nextGroups;
    });
  };

  const renderPermissionRow = (permission: string) => (
    <div className="bpa-permission-action" key={permission}>
      <span title={permission}>{permission}</span>
      <button
        type="button"
        className="bpa-permission-copy-button"
        aria-label={`Copy ${permission}`}
        title="Copy permission"
        onClick={() => copyPermission(permission)}
      >
        <ContentCopyOutlinedIcon fontSize="inherit" aria-hidden="true" />
      </button>
    </div>
  );

  const renderPermissionList = (
    categoryKey: PermissionCategoryKey,
    permissionList: string[],
  ) => {
    if (viewMode === 'flat') {
      return (
        <div className="bpa-permission-flat-list">
          {permissionList.map(renderPermissionRow)}
        </div>
      );
    }

    return (
      <div className="bpa-permission-group-list">
        {groupPermissions(permissionList).map(
          ([service, servicePermissions]) => {
            const groupKey = `${categoryKey}:${service}`;
            const isExpanded = expandedGroups.has(groupKey);

            return (
              <div className="bpa-permission-group" key={groupKey}>
                <button
                  type="button"
                  className="bpa-permission-group-header"
                  aria-expanded={isExpanded}
                  onClick={() => toggleGroup(groupKey)}
                >
                  {isExpanded ? (
                    <KeyboardArrowDownIcon aria-hidden="true" />
                  ) : (
                    <KeyboardArrowRightIcon aria-hidden="true" />
                  )}
                  <span className="bpa-permission-service-name">{service}</span>
                  <span className="bpa-permission-service-count">
                    {servicePermissions.length}
                  </span>
                </button>
                {isExpanded && (
                  <div className="bpa-permission-group-actions">
                    {servicePermissions.map(renderPermissionRow)}
                  </div>
                )}
              </div>
            );
          },
        )}
      </div>
    );
  };

  return (
    <section
      className="bpa-iam-permissions"
      aria-label="IAM permission details"
    >
      <div className="bpa-permission-toolbar">
        <div
          className="bpa-permission-view-toggle"
          role="group"
          aria-label="Permission list view"
        >
          <button
            type="button"
            className={viewMode === 'grouped' ? 'active' : ''}
            aria-pressed={viewMode === 'grouped'}
            onClick={() => setViewMode('grouped')}
          >
            <ViewListOutlinedIcon aria-hidden="true" />
            <span>Grouped</span>
          </button>
          <button
            type="button"
            className={viewMode === 'flat' ? 'active' : ''}
            aria-pressed={viewMode === 'flat'}
            onClick={() => setViewMode('flat')}
          >
            <FormatListBulletedOutlinedIcon aria-hidden="true" />
            <span>Flat list</span>
          </button>
        </div>

        <label className="bpa-permission-search">
          <SearchIcon aria-hidden="true" />
          <span className="sr-only">Search permissions</span>
          <input
            type="search"
            value={searchTerm}
            placeholder="Search permission"
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </label>

        <div className="bpa-permission-service-filter">
          <Select<ServiceFilterOption, false>
            inputId="bpa-permission-service-filter"
            aria-label="Filter permissions by service"
            value={selectedService}
            options={serviceOptions}
            placeholder="Filter by service"
            noOptionsMessage={() => 'No services found'}
            isClearable
            isSearchable
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={SERVICE_FILTER_STYLES}
            onChange={(option) => setServiceFilter(option?.value || '')}
          />
        </div>
      </div>

      <div className="bpa-permission-filter-note">
        <InfoOutlinedIcon aria-hidden="true" />
        <span className="bpa-permission-filter-note-text">
          Search and filters apply to all sections
        </span>
        {hasActiveFilters && (
          <button type="button" onClick={clearFilters}>
            <FilterAltOffOutlinedIcon aria-hidden="true" />
            <span>Clear filters</span>
          </button>
        )}
      </div>

      <div className="bpa-permission-columns">
        {PERMISSION_CATEGORIES.map((category) => {
          const permissionList = filteredPermissionLists[category.key];
          const isEmpty = permissionList.length === 0;

          return (
            <section
              className={`bpa-permission-column bpa-permission-column-${category.tone}`}
              aria-label={`${category.label} permissions`}
              key={category.key}
            >
              <header>
                <span className="bpa-permission-column-accent" />
                <h3>{category.label}</h3>
                <span className="bpa-permission-count">
                  {permissionList.length}
                </span>
              </header>
              <div className="bpa-permission-column-content">
                {isEmpty ? (
                  <div className="bpa-permission-empty-state">
                    <EmptyStateIcon tone={category.tone} />
                    <strong>
                      {hasActiveFilters
                        ? `No matching ${category.label.toLowerCase()} permissions`
                        : category.emptyTitle}
                    </strong>
                    <span>
                      {hasActiveFilters
                        ? 'Try changing the search or service filter.'
                        : category.emptyDescription}
                    </span>
                  </div>
                ) : (
                  renderPermissionList(category.key, permissionList)
                )}
              </div>
            </section>
          );
        })}
      </div>
    </section>
  );
};

export default BPAFindingIAMPermissions;
